import type { McpCatalog, McpCatalogEntry, McpPlaybookScope, McpPlaybookStep, McpPlaybookSummary } from '@/types/mcp'

export type StepSegment = { text: string; entry?: McpCatalogEntry }

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

export function catalogEntries(catalog: McpCatalog | undefined): Map<string, McpCatalogEntry> {
  return new Map([...(catalog?.measures ?? []), ...(catalog?.dimensions ?? [])].map((entry) => [entry.id, entry]))
}

/**
 * Returns a function that splits a step into plain text and catalog ids. Longest ids first, and an id
 * only counts as a whole token, so `debtors.aged.91` is never read as `debtors.aged` followed by ".91".
 */
export function catalogIdSplitter(entries: Map<string, McpCatalogEntry>): (text: string) => StepSegment[] {
  if (entries.size === 0) return (text) => [{ text }]
  const ids = [...entries.keys()].sort((a, b) => b.length - a.length).map(escapeRegExp)
  // The leading character is matched rather than looked behind at: Safari before 16.4 can't parse a lookbehind.
  const pattern = new RegExp(`(^|[^\\w.])(${ids.join('|')})(?!\\w|\\.\\w)`, 'g')
  return (text) => {
    const segments: StepSegment[] = []
    let last = 0
    for (const match of text.matchAll(pattern)) {
      const id = match[2] ?? ''
      const start = (match.index ?? 0) + (match[1] ?? '').length
      if (start > last) segments.push({ text: text.slice(last, start) })
      segments.push({ text: id, entry: entries.get(id) })
      last = start + id.length
    }
    if (last < text.length) segments.push({ text: text.slice(last) })
    return segments
  }
}

/** Inputs are filled in wherever `{name}` appears, ignoring case as the API does: the text, filter values and titles. */
export function unusedInputs(inputNames: string[], steps: McpPlaybookStep[]): string[] {
  const written = steps.map((step) => JSON.stringify(step).toLowerCase())
  return inputNames.filter((name) => name && !written.some((step) => step.includes(`{${name.toLowerCase()}}`)))
}

export function matchesSearch(entry: McpCatalogEntry, term: string) {
  const search = term.trim().toLowerCase()
  if (!search) return true
  return [entry.label, entry.id, ...entry.synonyms].some((text) => text.toLowerCase().includes(search))
}

export function insertAt(text: string, start: number, end: number, insertion: string): string {
  return `${text.slice(0, start)}${insertion}${text.slice(end)}`
}

export const runPrompt = (playbook: Pick<McpPlaybookSummary, 'name'>) => `run ${playbook.name}`

export const SCOPE_AUDIENCE: Record<McpPlaybookScope, string> = {
  starter: 'Everyone at the firm',
  firmEdit: 'Everyone at the firm',
  firm: 'Everyone at the firm',
  personal: 'Only me',
}
